import { anyUnpack } from '@bufbuild/protobuf/wkt'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import { useGrpcClients } from '@/contexts/GrpcClientContext'
import { GlobalConfigurationEntryDto_ColorDtoSchema } from '@/generated/Messages/Basic_pb'
import { assertNotNullish } from '@/helper/assertNotNullish'
import {
    primaryColorDarkGlobalSettingsKey,
    primaryColorLightGlobalSettingsKey,
    secondaryColorDarkGlobalSettingsKey,
    secondaryColorLightGlobalSettingsKey,
} from '@/helper/globalSettingsKeys'
import { configurationGlobalQueryKeyPrefix } from '@/helper/queryKeyPrefixes'

export const tenantColorSchemeQueryKeyPrefixes = [
    configurationGlobalQueryKeyPrefix,
    'COLOR_SCHEME',
] as const

export const useGlobalSettingsColorScheme = () => {
    const { configuration } = useGrpcClients()

    const { user } = useAuth()

    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return useSuspenseQuery({
        queryKey: [...tenantColorSchemeQueryKeyPrefixes, user],

        queryFn: async ({ signal }) => {
            if (!user) {
                return { dark: undefined, light: undefined }
            }

            assertNotNullish(configuration)

            try {
                const [
                    primaryColorLightRes,
                    secondaryColorLightRes,

                    primaryColorDarkRes,
                    secondaryColorDarkRes,
                ] = await Promise.all([
                    configuration.getGlobalConfigurationEntry(
                        {
                            configurationKey:
                                primaryColorLightGlobalSettingsKey,
                            tenantId: user.profile.tenant,
                        },
                        { signal }
                    ),
                    configuration.getGlobalConfigurationEntry(
                        {
                            configurationKey:
                                secondaryColorLightGlobalSettingsKey,
                            tenantId: user.profile.tenant,
                        },
                        { signal }
                    ),

                    configuration.getGlobalConfigurationEntry(
                        {
                            configurationKey: primaryColorDarkGlobalSettingsKey,
                            tenantId: user.profile.tenant,
                        },
                        { signal }
                    ),
                    configuration.getGlobalConfigurationEntry(
                        {
                            configurationKey:
                                secondaryColorDarkGlobalSettingsKey,
                            tenantId: user.profile.tenant,
                        },
                        { signal }
                    ),
                ])

                return {
                    light: {
                        primary:
                            primaryColorLightRes.configurationValue ?
                                anyUnpack(
                                    primaryColorLightRes.configurationValue,
                                    GlobalConfigurationEntryDto_ColorDtoSchema
                                )?.color
                            :   undefined,
                        secondary:
                            secondaryColorLightRes.configurationValue ?
                                anyUnpack(
                                    secondaryColorLightRes.configurationValue,
                                    GlobalConfigurationEntryDto_ColorDtoSchema
                                )?.color
                            :   undefined,
                    },
                    dark: {
                        primary:
                            primaryColorDarkRes.configurationValue ?
                                anyUnpack(
                                    primaryColorDarkRes.configurationValue,
                                    GlobalConfigurationEntryDto_ColorDtoSchema
                                )?.color
                            :   undefined,
                        secondary:
                            secondaryColorDarkRes.configurationValue ?
                                anyUnpack(
                                    secondaryColorDarkRes.configurationValue,
                                    GlobalConfigurationEntryDto_ColorDtoSchema
                                )?.color
                            :   undefined,
                    },
                }
            } catch {
                return { dark: undefined, light: undefined }
            }
        },
    })
}
